type C = { item: { id: string } }
type R<T extends C> = { key: string; items: T[] }

/**
 * Blind round 4 (kim, priya) + journey J2: a quiet refresh (a stale cached answer asked again after a Save, an area still
 * building) used to swap the whole list while the person was tapping, so a card moved or vanished and the next tap saved
 * the wrong one. A quiet answer now never moves what is on screen: every card already shown keeps its place (with the
 * newer data when the server still has it), cards the server dropped stay until the next visit, new cards go at the end
 * of their row. The server's own order is cached, so coming back to the view shows it (B-163: the list refreshes when the
 * person returns, never under a finger).
 */
export function mergeQuiet<T extends C, B extends { rows: R<T>[]; items: T[] }>(cur: B, got: B): B {
  const keep = (old: T[], fresh: T[]): T[] => {
    const byId = new Map(fresh.map((c) => [c.item.id, c]))
    const seen = new Set(old.map((c) => c.item.id))
    return [...old.map((c) => byId.get(c.item.id) ?? c), ...fresh.filter((c) => !seen.has(c.item.id))]
  }
  const gotRows = new Map(got.rows.map((r) => [r.key, r]))
  const curKeys = new Set(cur.rows.map((r) => r.key))
  const rows = [
    ...cur.rows.map((r) => ({ ...(gotRows.get(r.key) ?? r), items: keep(r.items, gotRows.get(r.key)?.items ?? []) })),
    ...got.rows.filter((r) => !curKeys.has(r.key)),
  ]
  return { ...got, rows, items: keep(cur.items, got.items) }
}
